import axios from 'axios';
import { 
  App, 
  InstalledApp, 
  AppFilters, 
  MarketplaceResponse,
  InstallResult,
  UninstallResult,
  AppProvidersResponse,
  AppCategory
} from '../types/apps';
import { sampleApps, sampleInstalledApps } from '../data/sampleApps';

// Additional interfaces for app service
interface BuilderService {
  id?: string;
  name: string;
  description: string;
  version?: string;
  appId?: string;
  type: string;
  configuration?: Record<string, unknown>;
  capabilities?: string[];
  apiEndpoints?: Record<string, string>;
}

interface BuilderIntegration {
  type: 'form' | 'rule' | 'page' | 'workflow';
  name: string;
  description: string;
  icon: string;
  available?: boolean;
  configuration?: Record<string, unknown>;
}

interface SecurityIssue {
  severity: 'low' | 'medium' | 'high' | 'critical';
  type: string;
  description: string;
  message: string;
  file?: string;
  line?: number;
  column?: number;
}

interface BuilderComponent {
  type: string;
  label: string;
  configuration: Record<string, unknown>;
}

interface AppReview {
  id: string;
  appId: string;
  userId: string;
  rating: number;
  review?: string;
  createdAt: string;
  user?: {
    name: string;
    avatar?: string;
  };
}

const API_BASE_URL = import.meta.env.VITE_API_URL;
const USE_MOCK_DATA = import.meta.env.VITE_ENVIRONMENT === 'development' && !import.meta.env.VITE_USE_REAL_API;

class AppService {
  private api = axios.create({
    baseURL: `${API_BASE_URL}/`,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    // Add request interceptor to include auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Get apps from marketplace
   */
  async getMarketplaceApps(filters?: AppFilters): Promise<MarketplaceResponse> {
    if (USE_MOCK_DATA) {
      return this.getMockMarketplaceApps(filters);
    }

    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const response = await this.api.get(`/apps/marketplace?${params.toString()}`);
    return response.data;
  }

  /**
   * Get specific app details
   */
  async getApp(appId: string): Promise<App> {
    if (USE_MOCK_DATA) {
      const app = sampleApps.find(p => p.id === appId);
      if (!app) {
        throw new Error('App not found');
      }
      return app;
    }

    const response = await this.api.get(`/apps/${appId}`);
    return response.data;
  }

  /**
   * Get installed apps for current user
   */
  async getInstalledApps(): Promise<InstalledApp[]> {
    if (USE_MOCK_DATA) {
      return sampleInstalledApps;
    }

    const response = await this.api.get('/apps/installed');
    return response.data;
  }

  /**
   * Install an app
   */
  async installApp(appId: string, version?: string, configuration?: Record<string, unknown>): Promise<InstallResult> {
    if (USE_MOCK_DATA) {
      const app = sampleApps.find(p => p.id === appId);
      if (!app) {
        return { success: false, error: 'App not found' };
      }

      // Simulate installation delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      return {
        success: true,
        app: {
          id: app.id,
          name: app.name,
          version: app.version,
          description: app.description
        },
        installation: {
          appId,
          version: version || app.version,
          isEnabled: true,
          installedAt: new Date().toISOString(),
          installedBy: 'current-user',
          configuration: configuration || {}
        }
      };
    }

    const response = await this.api.post(`/apps/${appId}/install`, {
      version,
      configuration,
    });
    return response.data;
  }

  /**
   * Uninstall an app
   */
  async uninstallApp(appId: string): Promise<UninstallResult> {
    if (USE_MOCK_DATA) {
      // Simulate uninstallation delay
      await new Promise(resolve => setTimeout(resolve, 800));
      return { success: true, message: 'App uninstalled successfully' };
    }

    const response = await this.api.delete(`/apps/${appId}/uninstall`);
    return response.data;
  }

  /**
   * Create a new app (for developers)
   */
  async createApp(appData: Partial<App>): Promise<App> {
    const response = await this.api.post('/apps', appData);
    return response.data;
  }

  /**
   * Get app providers for builders
   */
  async getAppProviders(providerType: string): Promise<AppProvidersResponse> {
    if (USE_MOCK_DATA) {
      return this.getMockAppProviders(providerType);
    }

    const response = await this.api.get(`/apps/providers/${providerType}`);
    return response.data;
  }

  /**
   * Get builder services information
   */
  async getBuilderServices(builderType: 'form' | 'rule' | 'page' | 'workflow'): Promise<BuilderService[]> {
    if (USE_MOCK_DATA) {
      return [this.getMockBuilderServices(builderType)];
    }

    const response = await this.api.get(`/apps/builder-services/${builderType}`);
    return response.data;
  }

  /**
   * Get available builder integrations
   */
  async getBuilderIntegrations(): Promise<BuilderIntegration[]> {
    if (USE_MOCK_DATA) {
      return [
        {
          type: 'form',
          name: 'Form Builder',
          description: 'Create dynamic forms with conditional logic',
          icon: 'description',
          available: true
        },
        {
          type: 'rule',
          name: 'Rule Builder',
          description: 'Create business rules with conditions and actions',
          icon: 'rule',
          available: true
        },
        {
          type: 'page',
          name: 'Page Builder',
          description: 'Design custom pages and interfaces',
          icon: 'web',
          available: true
        },
        {
          type: 'workflow',
          name: 'Workflow Builder',
          description: 'Create multi-step business processes',
          icon: 'account_tree',
          available: true
        }
      ];
    }

    const response = await this.api.get('/apps/builder-integrations');
    return response.data;
  }

  /**
   * Integrate builder components into an app
   */
  async integrateBuilderComponents(appId: string, builderType: string, components: BuilderComponent[]): Promise<void> {
    if (USE_MOCK_DATA) {
      // Simulate integration delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return;
    }

    await this.api.post(`/apps/${appId}/integrate/${builderType}`, {
      components
    });
  }

  /**
   * Search marketplace apps
   */
  async searchApps(query: string, filters?: Omit<AppFilters, 'search'>): Promise<MarketplaceResponse> {
    return this.getMarketplaceApps({ ...filters, search: query });
  }

  /**
   * Get apps by category
   */
  async getAppsByCategory(category: AppCategory, filters?: Omit<AppFilters, 'category'>): Promise<MarketplaceResponse> {
    return this.getMarketplaceApps({ ...filters, category });
  }

  /**
   * Get featured apps
   */
  async getFeaturedApps(): Promise<App[]> {
    const response = await this.getMarketplaceApps({ 
      limit: 10,
      // Sort by downloads or rating
    });
    return response.apps;
  }

  /**
   * Rate an app
   */
  async rateApp(appId: string, rating: number, review?: string): Promise<void> {
    await this.api.post(`/apps/${appId}/rate`, {
      rating,
      review,
    });
  }

  /**
   * Get app reviews
   */
  async getAppReviews(appId: string): Promise<AppReview[]> {
    const response = await this.api.get(`/apps/${appId}/reviews`);
    return response.data;
  }

  /**
   * Enable/disable an installed app
   */
  async toggleApp(appId: string, enabled: boolean): Promise<void> {
    await this.api.patch(`/apps/installed/${appId}`, {
      isEnabled: enabled,
    });
  }

  /**
   * Update app configuration
   */
  async updateAppConfiguration(appId: string, configuration: Record<string, unknown>): Promise<void> {
    await this.api.patch(`/apps/installed/${appId}/configuration`, {
      configuration,
    });
  }

  /**
   * Validate JavaScript code for app builder
   */
  async validateJavaScriptCode(
    code: string, 
    options?: { 
      hookType?: string; 
      functionName?: string; 
      appId?: string; 
    }
  ): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
    securityIssues: Array<{
      type: string;
      message: string;
      severity: 'low' | 'medium' | 'high' | 'critical';
      line?: number;
      column?: number;
    }>;
    functionNameValidation?: { isValid: boolean; message?: string };
    safeTemplate?: string;
    sanitizedCode?: string;
  }> {
    if (USE_MOCK_DATA) {
      // Mock validation for development
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Simple mock validation
      const errors: string[] = [];
      const warnings: string[] = [];
      const securityIssues: SecurityIssue[] = [];
      
      if (!code || !code.trim()) {
        errors.push('Code cannot be empty');
      }
      
      if (code.includes('eval(')) {
        securityIssues.push({
          type: 'EVAL_USAGE',
          description: 'eval() is not allowed for security reasons',
          message: 'eval() is not allowed for security reasons',
          severity: 'critical',
          line: 1
        });
        errors.push('Security violation: eval() is not allowed');
      }
      
      if (code.includes('setTimeout') || code.includes('setInterval')) {
        warnings.push('Timers are disabled in the sandbox environment');
      }
      
      return {
        isValid: errors.length === 0,
        errors,
        warnings,
        securityIssues,
        functionNameValidation: options?.functionName ? { isValid: true } : undefined,
        sanitizedCode: code
      };
    }

    const response = await this.api.post('/apps/validate-javascript', {
      code,
      hookType: options?.hookType,
      functionName: options?.functionName,
      appId: options?.appId
    });
    
    return response.data;
  }

  // Legacy methods for backward compatibility
  async getMarketplacePlugins(filters?: AppFilters): Promise<MarketplaceResponse> {
    return this.getMarketplaceApps(filters);
  }

  async getPlugin(pluginId: string): Promise<App> {
    return this.getApp(pluginId);
  }

  async getInstalledPlugins(): Promise<InstalledApp[]> {
    return this.getInstalledApps();
  }

  async installPlugin(pluginId: string, version?: string, configuration?: Record<string, unknown>): Promise<InstallResult> {
    return this.installApp(pluginId, version, configuration);
  }

  async uninstallPlugin(pluginId: string): Promise<UninstallResult> {
    return this.uninstallApp(pluginId);
  }

  async getPluginProviders(type: string): Promise<AppProvidersResponse> {
    return this.getAppProviders(type);
  }

  // Mock data methods for development
  private getMockMarketplaceApps(filters?: AppFilters): MarketplaceResponse {
    let apps = [...sampleApps];

    // Apply filters
    if (filters?.category) {
      apps = apps.filter(p => p.category === filters.category);
    }

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      apps = apps.filter(p => 
        p.name.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower)
      );
    }

    if (filters?.maxPrice !== undefined) {
      if (filters.maxPrice === 0) {
        apps = apps.filter(p => p.pricing.model === 'free');
      } else {
        apps = apps.filter(p => 
          p.pricing.model === 'free' || 
          (p.pricing.price && p.pricing.price <= filters.maxPrice!)
        );
      }
    }

    // Pagination
    const page = filters?.page || 1;
    const limit = filters?.limit || 12;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedApps = apps.slice(startIndex, endIndex);

    return {
      apps: paginatedApps,
      pagination: {
        page,
        limit,
        total: apps.length,
        pages: Math.ceil(apps.length / limit)
      }
    };
  }

  private getMockAppProviders(providerType: string): AppProvidersResponse {
    const installedApps = sampleInstalledApps.filter(p => p.isEnabled);
    
    // TEMPORARY MIGRATION CODE:
    // The following mapping from provider types to categories exists for legacy compatibility.
    // This should be removed once all dependencies on old AppType values are eliminated.
    const categoryMap: Record<string, string> = {
      'form-field': 'foundational',
      'rule-action': 'loan-processing', 
      'workflow-step': 'workflow',
      'validation': 'foundational',
      'integration': 'integration',
      'report': 'utility',
      'utility': 'utility',
      'file-manager': 'utility',
      'page': 'utility'
    };

    const targetCategory = categoryMap[providerType] || providerType;
    
    const providers = installedApps
      .filter(p => p.app?.category === targetCategory)
      .map(p => ({
        appId: p.appId,
        providerType: `custom-${p.app!.name.toLowerCase().replace(/\s+/g, '-')}`,
        label: p.app!.name,
        description: p.app!.description,
        icon: 'Extension',
      }));

    return {
      providerType,
      providers
    };
  }

  private getMockBuilderServices(builderType: string): BuilderService {
    const services: Record<string, BuilderService> = {
      form: {
        type: 'form',
        name: 'Form Builder',
        description: 'Create dynamic forms with conditional logic',
        capabilities: ['field-types', 'validation', 'conditional-logic', 'multi-step'],
        apiEndpoints: {
          create: '/api/v1/forms',
          list: '/api/v1/forms',
          get: '/api/v1/forms/{id}',
          update: '/api/v1/forms/{id}',
          delete: '/api/v1/forms/{id}'
        }
      },
      rule: {
        type: 'rule',
        name: 'Rule Builder',
        description: 'Create business rules with conditions and actions',
        capabilities: ['conditions', 'actions', 'triggers', 'priorities'],
        apiEndpoints: {
          create: '/api/v1/rules',
          list: '/api/v1/rules',
          get: '/api/v1/rules/{id}',
          update: '/api/v1/rules/{id}',
          delete: '/api/v1/rules/{id}',
          execute: '/api/v1/rules/{id}/execute'
        }
      },
      page: {
        type: 'page',
        name: 'Page Builder',
        description: 'Design custom pages and interfaces',
        capabilities: ['layouts', 'components', 'styling', 'navigation'],
        apiEndpoints: {
          create: '/api/v1/pages',
          list: '/api/v1/pages',
          get: '/api/v1/pages/{id}',
          update: '/api/v1/pages/{id}',
          delete: '/api/v1/pages/{id}'
        }
      },
      workflow: {
        type: 'workflow',
        name: 'Workflow Builder',
        description: 'Create multi-step business processes',
        capabilities: ['steps', 'transitions', 'conditions', 'parallel-execution'],
        apiEndpoints: {
          create: '/api/v1/workflows',
          list: '/api/v1/workflows',
          get: '/api/v1/workflows/{id}',
          update: '/api/v1/workflows/{id}',
          delete: '/api/v1/workflows/{id}',
          execute: '/api/v1/workflows/{id}/execute'
        }
      }
    };

    return services[builderType] || {
      type: builderType,
      name: 'Unknown Service',
      description: 'Service not found'
    };
  }
}

export const appService = new AppService();
export const pluginService = appService; // Legacy compatibility
export default AppService;